import type { Container, CreateDocumentInput, DocumentUpdate } from "@shared/types/supabase.types";
import { supabase } from "@/lib/supabase";
import { getContainerMemberByContainerId } from "@/features/containers/api/containers-api";

export const docsApi = {
  // Get a single document container by ID
  async getDocument(documentId: string): Promise<Container> {
    const { data, error } = await supabase
      .from("containers")
      .select("*")
      .eq("id", documentId)
      .eq("container_type", "document")
      .is("deleted_at", null)
      .single();

    if (error) throw error;
    return data;
  },

  // List all document containers inside a parent container (e.g. a project)
  async listDocuments(parentContainerId: string): Promise<Container[]> {
    const { data, error } = await supabase
      .from("containers")
      .select("*")
      .eq("container_type", "document")
      .eq("parent_container_id", parentContainerId)
      .is("deleted_at", null)
      .order("name")
      .order("created_at");

    if (error) throw error;
    return data;
  },

  // Create a new document container via the create_document RPC
  async createDocument(input: CreateDocumentInput): Promise<Container> {
    const { data, error } = await supabase.rpc("create_document", input);

    if (error) throw error;
    return data as Container;
  },

  // Update document metadata (name, soft-delete, last_edited_by).
  // Content changes go through Hocuspocus / document_versions — not here.
  async updateDocument(
    userId: string,
    parentContainerId: string,
    documentId: string,
    updates: DocumentUpdate,
  ): Promise<Container> {
    const containerMember = await getContainerMemberByContainerId(userId, parentContainerId);

    const { data, error } = await supabase
      .from("containers")
      .update({
        ...updates,
        last_edited_by: containerMember.id,
        updated_at: new Date().toISOString(),
      })
      .eq("id", documentId)
      .eq("container_type", "document")
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Soft-delete a document via the delete_document SECURITY DEFINER RPC.
  // A direct UPDATE cannot be used here because PostgreSQL implicitly applies
  // the SELECT policy's USING expression (which requires deleted_at IS NULL)
  // as a WITH CHECK on UPDATE commands — blocking any attempt to set
  // deleted_at to a non-null value through the normal RLS path.
  // The RPC runs as SECURITY DEFINER and enforces creator-or-owner/admin
  // authorisation internally, bypassing that RLS conflict.
  async deleteDocument(documentId: string): Promise<void> {
    const { error } = await supabase.rpc("delete_document", {
      p_document_id: documentId,
    });

    if (error) throw error;
  },
};
